import { useCallback, useState } from "react"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { SharedAddConnectionDialog, AddConnectionSubmitPayload } from "@hydrologiq/shared/data-connections/AddConnectionDialog"
import {
  useAvailableConnectors,
  useFreeInstances,
  useCreateAssetConnection,
} from "../../hooks/useAssetDataConnection"

interface AddAssetConnectionDialogProps {
  assetId: string
  /** The asset's class, used to hide connectors that could never serve it. */
  assetClass: string | null | undefined
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}

export function AddAssetConnectionDialog({
  assetId,
  assetClass,
  isOpen,
  onClose,
  onCreated,
}: AddAssetConnectionDialogProps) {
  const [selectedMonitorId, setSelectedMonitorId] = useState("")
  const { connectors, loadingConnectors } = useAvailableConnectors()
  const { freeInstances, loadingFree } = useFreeInstances(selectedMonitorId)
  const { createConnection, creating } = useCreateAssetConnection(assetId)

  const handleSubmit = useCallback(
    async (payload: AddConnectionSubmitPayload) => {
      try {
        await createConnection({
          assetId,
          monitorId: payload.monitorId,
          externalId: payload.externalId,
          monitoringInstanceId: payload.monitoringInstanceId,
          deviceModel: payload.deviceModel,
        })
        toaster.success({ title: "Data connection created" })
        onCreated()
      } catch (err: any) {
        const message = err?.message || "Failed to create data connection"
        toaster.error({ title: "Error", description: message })
        throw err
      }
    },
    [assetId, createConnection, onCreated],
  )

  return (
    <SharedAddConnectionDialog
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      connectors={connectors}
      loadingConnectors={loadingConnectors}
      unlinkedInstances={freeInstances}
      loadingUnlinked={loadingFree}
      onConnectorChange={setSelectedMonitorId}
      creating={creating}
      assetClass={assetClass}
      showConnectorTypeSelect={true}
    />
  )
}

export default AddAssetConnectionDialog
